import { useLabelVersion } from '../../../../../features/labels/LabelsProvider';
import { labelId } from '../../../../../features/labels/labels';
﻿import React, { useState, useMemo } from "react";
import {
  renderAggregatesCentral,
  renderOuterArcsAndLabels,
  renderDashedArcs,
  renderDayBars,
  renderBubbleSeries,
  renderSelectedTable,
  transformCenterData
} from "./utils/polarChartUtils";

import { ALL_METRICS, isFedDay, sanitizeSfr } from "../../constants/constantsAnalytic";

const START_ANGLE = 0;
const END_ANGLE = 360;
const GAP_ANGLE = 2;       // 2Â° de espacio entre segmentos (jaulas)
const PADDING_ANGLE = 3;   // 3Â° de margen extra al inicio y final de cada segmento

const deg2rad = (deg) => (deg * Math.PI) / 180;
const VIEWBOX_SIZE = 680;
const CX = VIEWBOX_SIZE / 2;
const CY = VIEWBOX_SIZE / 2;
const R_OUTER = 280;
const R_OUTER_INNER = 290;

// Anillo medio â€œgruesoâ€ para repartir varias mÃ©tricas
const R_MID = 260;      // Radio exterior del â€œanillo medioâ€
const R_MID_INNER = 170;// Radio interior del â€œanillo medioâ€
const R_INNER = 150;    // Radio del cÃ­rculo interior


function polarToCartesian(r, angleDeg) {
  const angleRad = deg2rad(angleDeg - 90);
  return {
    x: CX + r * Math.cos(angleRad),
    y: CY + r * Math.sin(angleRad),
  };
}

function useMetricMaxima(data, metricKeys) {
  return useMemo(() => {
    const maxima = {};
    for (const m of metricKeys) {
      let maxVal = 0;
      data?.forEach((serie) =>
        (serie?.values_summary || []).forEach((item) => {
          // delivered_food ya viene en [ton]; SFR saneado (>1000% recalculado/descartado) para no inflar la escala
          let v = m === "sfr_value" ? sanitizeSfr(item.value) : Number(item.value[m]);
          if (Number.isFinite(v) && v > maxVal) maxVal = v;
        })
      );
      maxima[m] = maxVal > 0 ? maxVal : 1;
    }
    return maxima;
  }, [data, metricKeys]);
}

function mapValueToBubbleRadius(val, maxVal, isDeliveredFood) {
  // delivered_food ya viene en [ton]; no se divide de nuevo
  let v = Number(val);
  if (isNaN(v)) v = 0;
  const t = Math.min(Math.max(v / maxVal, 0), 1);
  const rMin = 3.5;
  const rMax = 8;
  return rMin + t * (rMax - rMin);
}

function describeOuterArc(startAngle, endAngle, color) {
  const p1 = polarToCartesian(R_OUTER, startAngle,  );
  const p2 = polarToCartesian(R_OUTER, endAngle,  );
  const p3 = polarToCartesian(R_OUTER_INNER, endAngle,  );
  const p4 = polarToCartesian(R_OUTER_INNER, startAngle,  );
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return (
    <path
      d={[
        "M", p1.x.toFixed(3), p1.y.toFixed(3),
        "A", R_OUTER.toFixed(3), R_OUTER.toFixed(3), 0, largeArcFlag, 1, p2.x.toFixed(3), p2.y.toFixed(3),
        "L", p3.x.toFixed(3), p3.y.toFixed(3),
        "A", R_OUTER_INNER.toFixed(3), R_OUTER_INNER.toFixed(3), 0, largeArcFlag, 0, p4.x.toFixed(3), p4.y.toFixed(3),
        "Z"
      ].join(" ")}
      fill={color}
      stroke="none"
    />
  );
}

const AdvancedPolarChart = ({ colors = [], versus, selected, nivelSeleccionado, excludedMetrics, setExcludedMetrics,
    hoverInfo, setHoverInfo,
    selectedInfo, setSelectedInfo,
    hoverDayInfo, setHoverDayInfo, translate }) => {
  useLabelVersion();
  const t = (key, fallback) => {
    const value = typeof translate === "function" ? translate(key) : key;
    return value && value !== key ? value : fallback;
  };
  // ---------- ESTADOS ----------


  // --------------------------------------------------
  // Filtrar mÃ©tricas ocultas
  // const visibleMetrics = ALL_METRICS.filter((m) => !excludedMetrics.includes(m.key));}
  const visibleMetrics = ALL_METRICS?.filter(
    (m) => m.vsMetrics && !excludedMetrics.includes(m.key)
  );
  const Mvis = visibleMetrics.length;

  // const metricColor = ALL_METRICS?.find(m => m.key === hoverInfo.metricKey)?.color || "#ccc";
  // --------------------------------------------------
  // Filtrar â€œjaulas seleccionadasâ€ y tomar hasta 4
  const displayedSeries = useMemo(() => {
    if (nivelSeleccionado === 'region') {
      // Aplanar todas las jaulas de cada centro seleccionado
      const processed = [];

      versus
        .filter(center => selected.includes(center.name))
        .forEach(center => {
          const cages = center.analytic_data || {};

          Object.entries(cages).forEach(([jaulaId, jaula]) => {
            const vs = jaula.values_summary;

            const values_summary = vs && typeof vs === 'object' && !Array.isArray(vs)
              ? Object.entries(vs).map(([day, value]) => ({ day, value }))
              : Array.isArray(vs) ? vs : [];

            processed.push({
              name: jaula.name || jaulaId,
              values_summary
            });
          });
        });

      return processed.slice(0, 4);
    }
    else if( nivelSeleccionado === 'area'){
      // Agrupar y acumular datos por centro
      const groupedData = transformCenterData(versus);
      // Filtrar solo los centros seleccionados y tomar hasta 4
      return groupedData.filter((center) => selected.includes(center.seriesName)).slice(0, 4);
    } else if( nivelSeleccionado === 'centro'){
      return versus
      .filter((serieObj) => selected.includes(serieObj.name))
      .slice(0, 4);
    }
    
  }, [nivelSeleccionado, versus, selected]);

  console.log('displayedSeries',displayedSeries)

  // --------------------------------------------------
  const metricMaxima = useMetricMaxima(
    displayedSeries,
    visibleMetrics.map((m) => m.key)
  );

  // --------------------------------------------------
  // Espacio radial dinÃ¡mico (multiplicador para mÃ¡s separaciÃ³n)
  const spacing = Mvis > 0
    ? ((R_MID - R_MID_INNER) / Mvis) * 1.2
    : 0;
  const computeMetricRadius = (mIdx) => R_MID_INNER + spacing * (mIdx + 0.5);


  // --------------------------------------------------
  // 1) Calcular agregados por jaula (serie) para todas las mÃ©tricas
  const aggregatedBySeries = useMemo(() => {
    return displayedSeries?.map((serieObj) => {
      const sums = {
        delivered_food: 0,
        alarms_count: 0,
        perdidos_count: 0,
        visitas_count: 0,
      };
      let sfrSum = 0;
      let sfrCount = 0;

      serieObj?.values_summary?.forEach((item) => {
        const vals = item.value;
        const df = Number(vals.delivered_food);
        if (!isNaN(df)) sums.delivered_food += df;

        const ac = Number(vals.alarms_count);
        if (!isNaN(ac)) sums.alarms_count += ac;

        const pc = Number(vals.perdidos_count);
        if (!isNaN(pc)) sums.perdidos_count += pc;

        const vc = Number(vals.visitas_count);
        if (!isNaN(vc)) sums.visitas_count += vc;

        // SFR promedio: solo días alimentados (excluye ayunos) y con SFR saneado (>1000% recalculado/descartado)
        const sv = sanitizeSfr(vals);
        if (isFedDay(vals) && Number.isFinite(sv)) { // null (SFR irrecuperable) se ignora
          sfrSum += sv;
          sfrCount += 1;
        }
      });
      console.log('serieObj',serieObj)
      return {
        seriesName:    serieObj.name || serieObj.seriesName,
        delivered_food: sums.delivered_food,
        alarms_count:   sums.alarms_count,
        perdidos_count:  sums.perdidos_count,
        visitas_count:   sums.visitas_count,
        sfr_value:    sfrCount > 0 ? sfrSum / sfrCount : 0,
      };
    });
  }, [displayedSeries]);

  // --------------------------------------------------
  // 2) Calcular startAngles y endAngles dinÃ¡micos con GAP_ANGLE y PADDING_ANGLE
  const { startAngles, endAngles } = useMemo(() => {
    const count = displayedSeries?.length;
    if (count === 0) return { startAngles: [], endAngles: [] };

    // Definir pesos segÃºn count
    let weights = [];
    if (count === 1) {
      weights = [1];
    } else if (count === 2) {
      weights = [1, 1];
    } else if (count === 3) {
      weights = [2, 1, 1];
    } else {
      weights = [1, 1, 1, 1];
    }

    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    const totalGapSum = count * GAP_ANGLE;
    const totalPaddingSum = count * 2 * PADDING_ANGLE;
    const availableArc = (END_ANGLE - START_ANGLE) - totalGapSum - totalPaddingSum;
    const unitArc = availableArc / totalWeight;
    const segmentFullArc = weights.map((w) => 2 * PADDING_ANGLE + w * unitArc);

    const startAngles = [];
    const endAngles = [];
    let cursor = GAP_ANGLE / 2;

    for (let i = 0; i < count; i++) {
      const sA = cursor;
      const eA = sA + segmentFullArc[i];
      startAngles.push(sA);
      endAngles.push(eA);
      cursor = eA + GAP_ANGLE;
    }

    return { startAngles, endAngles };
  }, [displayedSeries]);

  const renderDayBarsProps = {displayedSeries,colors, startAngles,endAngles,PADDING_ANGLE,R_INNER,R_MID_INNER,visibleMetrics,setHoverDayInfo,setHoverInfo,setSelectedInfo,polarToCartesian,translate}
  const renderBubbleSeriesProps = {displayedSeries, startAngles,endAngles,PADDING_ANGLE,visibleMetrics,setHoverDayInfo,setHoverInfo,setSelectedInfo,metricMaxima,computeMetricRadius,hoverInfo,selectedInfo, mapValueToBubbleRadius,polarToCartesian,translate}
  const renderDashedArcsProps = { displayedSeries, startAngles,endAngles,PADDING_ANGLE,visibleMetrics,computeMetricRadius,polarToCartesian}
  const renderOuterArcsAndLabelsProps = {displayedSeries, startAngles,endAngles,colors,R_OUTER,describeOuterArc,polarToCartesian, nivelSeleccionado}
  const renderSelectedTableProps = {displayedSeries,selectedInfo,colors,CY,CX,nivelSeleccionado,translate }
  const renderAggregatesCentralProps = {colors,aggregatedBySeries, nivelSeleccionado, translate}

  return (
    <div
      style={{
        height: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        borderRadius: "1vw",
        paddingBottom: "2vh",
        marginTop: "-1vh",
      }}
    >
      <div
        style={{
          width: '33vw',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          flexDirection: 'row',
          marginBottom: "-1.5vh",
        }}
      >
        {renderAggregatesCentral(renderAggregatesCentralProps)}
      </div>

      <svg
        style={{ marginLeft: '-1vw' }}
        width="32vw"
        height="25vw"
        viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      >
        <defs>
          <radialGradient id="grad-inner" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1f1836" />
            <stop offset="100%" stopColor="#45445d" />
          </radialGradient>
          <radialGradient id="grad-mid" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#45445d" />
            <stop offset="100%" stopColor="#1f1836" />
          </radialGradient>
        </defs>

        {/* Pane exterior â€“ anillo oscuro */}
        <circle
          cx={CX}
          cy={CY}
          r={R_OUTER}
          fill="#46465C"
          stroke="#2E2C3A"
          strokeWidth="2"
        />
        <circle
          cx={CX}
          cy={CY}
          r={R_OUTER_INNER}
          fill="#1f1836"
          stroke="none"
        />

        {/* Arcos exteriores y etiquetas de jaula */}
        {renderOuterArcsAndLabels(renderOuterArcsAndLabelsProps)}

        {/* Pane medio â€“ fondo de mÃ©tricas */}
        <path
          d={(() => {
            const angleStart = START_ANGLE;
            const angleEnd = END_ANGLE;
            const p1 = polarToCartesian(R_MID, angleStart, );
            const p2 = polarToCartesian(R_MID, angleEnd, );
            const p3 = polarToCartesian(R_MID_INNER, angleEnd, );
            const p4 = polarToCartesian(R_MID_INNER, angleStart, );
            const largeArcFlag = angleEnd - angleStart <= 180 ? "0" : "1";
            return [
              "M", p1.x.toFixed(3), p1.y.toFixed(3),
              "A", R_MID.toFixed(3), R_MID.toFixed(3), 0, largeArcFlag, 1, p2.x.toFixed(3), p2.y.toFixed(3),
              "L", p3.x.toFixed(3), p3.y.toFixed(3),
              "A", R_MID_INNER.toFixed(3), R_MID_INNER.toFixed(3), 0, largeArcFlag, 0, p4.x.toFixed(3), p4.y.toFixed(3),
              "Z"
            ].join(" ");
          })()}
          fill="url(#grad-mid)"
          stroke="none"
        />

        {/* Pane interior â€“ cÃ­rculo central */}
        <circle
          cx={CX}
          cy={CY}
          r={R_INNER}
          fill="url(#grad-inner)"
          stroke="#2E2C3A"
          strokeWidth="1"
        />

        {/* Arcos punteados por mÃ©trica */}
        {renderDashedArcs(renderDashedArcsProps)}

        {/* Barras radiales de dÃ­a */}
        {renderDayBars(renderDayBarsProps)}

        {/* Burbujas de mÃ©tricas */}
        {renderBubbleSeries(renderBubbleSeriesProps)}

        {/* Tooltip central en hover de una burbuja */}
        {hoverInfo && !selectedInfo && (
          <g>
            <text
              x={CX}
              y={CY - 35}
              textAnchor="middle"
              fontSize="1vw"
              fill="#e84e0e"
              fontWeight="700"
            >
              {t('day', 'Dia')}: {hoverInfo.day}
            </text>
            <text
              x={CX}
              y={CY - 5}
              textAnchor="middle"
              fontSize="0.9vw"
              fill="#c3c3c3"
              fontWeight="600"
            >
              {labelId(hoverInfo.seriesName, "rac", hoverInfo.seriesName)}
            </text>
            <text
              x={CX}
              y={CY + 25}
              textAnchor="middle"
              fontSize="0.63vw"
              // fill={METRIC_COLORS[hoverInfo.metricKey]}
              fill={ALL_METRICS.find(m => m.key === hoverInfo.metricKey)?.color || "#ccc"}
              fontWeight="600"
            >
              {`${hoverInfo.metricLabel}:`}
            </text>
            <text
              x={CX}
              y={CY + 50}
              textAnchor="middle"
              fontSize="0.63vw"
              // fill={METRIC_COLORS[hoverInfo.metricKey]}
              fill={ALL_METRICS.find(m => m.key === hoverInfo.metricKey)?.color || "#ccc"}
              fontWeight="600"
            >
              {`${hoverInfo.metricValue}`}
            </text>
          </g>
        )}

        {/* Tooltip central en hover de â€œbarraâ€ (todos los valores) */}
        {hoverDayInfo && selectedInfo === null && (
          <g>
            <text
              x={CX}
              y={CY - 65}
              textAnchor="middle"
              fontSize="1vw"
              fill="#e84e0e"
              fontWeight="700"
            >
              {t('day', 'Dia')}: {hoverDayInfo.day}
            </text>
            <text
              x={CX}
              y={CY - 35}
              textAnchor="middle"
              fontSize="0.9vw"
              fill="#c3c3c3"
              fontWeight="600"
            >
              {labelId(hoverDayInfo.seriesName, "rac", hoverDayInfo.seriesName)}
            </text>
            {hoverDayInfo.allValues.map((mObj, i) => (
              <text
                key={`all-${i}`}
                x={CX}
                y={CY + (i * 25) - 2}
                textAnchor="middle"
                fontSize="0.63vw"
                fill={mObj.color}
                fontWeight="500"
              >
                {`${mObj.label}: ${mObj.value}`}
              </text>
            ))}
          </g>
        )}
        {/* CUANDO selectedInfo existe, mostramos tabla de valores */}
        {renderSelectedTable(renderSelectedTableProps)}
      </svg>
    </div>
  );
};

export default AdvancedPolarChart;


// import React, { useState, useMemo } from "react";
// import {   
//   renderAggregatesCentral,
//   renderOuterArcsAndLabels,
//   renderDashedArcs,
//   renderDayBars,
//   renderBubbleSeries,
//   renderSelectedTable,
//   transformCenterData
// } from "./utils/polarChartUtils";
// import { ALL_METRICS } from "../../constants/constantsAnalytic";

// const START_ANGLE = 0;
// const END_ANGLE = 360;
// const GAP_ANGLE = 2;       // 2Â° de espacio entre segmentos (jaulas)
// const PADDING_ANGLE = 3;   // 3Â° de margen extra al inicio y final de cada segmento

// const deg2rad = (deg) => (deg * Math.PI) / 180;
// const VIEWBOX_SIZE = 680;
// const CX = VIEWBOX_SIZE / 2;
// const CY = VIEWBOX_SIZE / 2;
// const R_OUTER = 280;
// const R_OUTER_INNER = 290;

// // Anillo medio â€œgruesoâ€ para repartir varias mÃ©tricas
// const R_MID = 260;      // Radio exterior del â€œanillo medioâ€
// const R_MID_INNER = 170;// Radio interior del â€œanillo medioâ€
// const R_INNER = 150;    // Radio del cÃ­rculo interior


// function polarToCartesian(r, angleDeg) {
//   const angleRad = deg2rad(angleDeg - 90);
//   return {
//     x: CX + r * Math.cos(angleRad),
//     y: CY + r * Math.sin(angleRad),
//   };
// }

// function useMetricMaxima(data, metricKeys) {
//   return useMemo(() => {
//     const maxima = {};
//     for (const m of metricKeys) {
//       let maxVal = 0;
//       data?.forEach((serie) =>
//         (serie?.values_summary || []).forEach((item) => {
//           let v = Number(item.value[m]);
//           if (m === "delivered_food") {
//             v = v ;/// 1000;
//           }
//           if (!isNaN(v) && v > maxVal) maxVal = v;
//         })
//       );
//       maxima[m] = maxVal > 0 ? maxVal : 1;
//     }
//     return maxima;
//   }, [data, metricKeys]);
// }

// function mapValueToBubbleRadius(val, maxVal, isDeliveredFood) {
//   let v = Number(val);
//   if (isDeliveredFood) {
//     v = v ;// 1000;
//   }
//   if (isNaN(v)) v = 0;
//   const t = Math.min(Math.max(v / maxVal, 0), 1);
//   const rMin = 3.5;
//   const rMax = 8;
//   return rMin + t * (rMax - rMin);
// }

// function describeOuterArc(startAngle, endAngle, color) {
//   const p1 = polarToCartesian(R_OUTER, startAngle,  );
//   const p2 = polarToCartesian(R_OUTER, endAngle,  );
//   const p3 = polarToCartesian(R_OUTER_INNER, endAngle,  );
//   const p4 = polarToCartesian(R_OUTER_INNER, startAngle,  );
//   const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

//   return (
//     <path
//       d={[
//         "M", p1.x.toFixed(3), p1.y.toFixed(3),
//         "A", R_OUTER.toFixed(3), R_OUTER.toFixed(3), 0, largeArcFlag, 1, p2.x.toFixed(3), p2.y.toFixed(3),
//         "L", p3.x.toFixed(3), p3.y.toFixed(3),
//         "A", R_OUTER_INNER.toFixed(3), R_OUTER_INNER.toFixed(3), 0, largeArcFlag, 0, p4.x.toFixed(3), p4.y.toFixed(3),
//         "Z"
//       ].join(" ")}
//       fill={color}
//       stroke="none"
//     />
//   );
// }

// const AdvancedPolarChart = ({ colors = [], versus, selected, excludedMetrics, setExcludedMetrics,
//     hoverInfo, setHoverInfo,
//     selectedInfo, setSelectedInfo,
//     hoverDayInfo, setHoverDayInfo, }) => {
//   // ---------- ESTADOS ----------


//   // console.log('versus',versus)
//   // console.log('selected',selected)
//   // console.log('versus',versus)

//   // --------------------------------------------------
//   // Filtrar mÃ©tricas ocultas
//   const visibleMetrics = ALL_METRICS.filter((m) => !excludedMetrics.includes(m.key));
//   const Mvis = visibleMetrics.length;

//   // --------------------------------------------------
//   // Filtrar â€œjaulas seleccionadasâ€ y tomar hasta 4
//   const displayedSeries = useMemo(
//     () => versus.filter(s => selected.includes(s.name)).slice(0, 4),
//     [versus, selected]
//   );

//   // console.log('displayedSeries',displayedSeries)

//   // --------------------------------------------------
//   const metricMaxima = useMetricMaxima(
//     displayedSeries,
//     visibleMetrics.map((m) => m.key)
//   );

//   // --------------------------------------------------
//   // Espacio radial dinÃ¡mico (multiplicador para mÃ¡s separaciÃ³n)
//   const spacing = Mvis > 0
//     ? ((R_MID - R_MID_INNER) / Mvis) * 1.2
//     : 0;
//   const computeMetricRadius = (mIdx) => R_MID_INNER + spacing * (mIdx + 0.5);


//   // --------------------------------------------------
//   // 1) Calcular agregados por jaula (serie) para todas las mÃ©tricas
//   const aggregatedBySeries = useMemo(() => {
//     return displayedSeries?.map((serieObj) => {
//       const sums = {
//         delivered_food: 0,
//         alarms_count: 0,
//         perdidos_count: 0,
//         visitas_count: 0,
//       };
//       let sfrSum = 0;
//       let sfrCount = 0;

//       serieObj?.values_summary?.forEach((item) => {
//         const vals = item.value;
//         const df = Number(vals.delivered_food);
//         if (!isNaN(df)) sums.delivered_food += df;

//         const ac = Number(vals.alarms_count);
//         if (!isNaN(ac)) sums.alarms_count += ac;

//         // const pc = Number(vals.perdidos_count);
//         // if (!isNaN(pc)) sums.perdidos_count += pc;

//         // const vc = Number(vals.visitas_count);
//         // if (!isNaN(vc)) sums.visitas_count += vc;
//         const pc = Number(vals.perdidos_count ?? vals.perdidos_count);
//         if (!isNaN(pc)) sums.perdidos_count += pc;

//         const vc = Number(vals.visitas_count ?? vals.visitas_count);
//         if (!isNaN(vc)) sums.visitas_count += vc;


//         const sv = Number(vals.sfr_value);
//         if (!isNaN(sv)) {
//           sfrSum += sv;
//           sfrCount += 1;
//         }
//       });
//       // console.log('serieObj',serieObj)
//       return {
//         seriesName:    serieObj.name || serieObj.seriesName,
//         delivered_food: sums.delivered_food,
//         alarms_count:   sums.alarms_count,
//         perdidos_count:  sums.perdidos_count,
//         visitas_count:   sums.visitas_count,
//         sfr_value:    sfrCount > 0 ? sfrSum / sfrCount : 0,
//       };
//     });
//   }, [displayedSeries]);

//   // --------------------------------------------------
//   // 2) Calcular startAngles y endAngles dinÃ¡micos con GAP_ANGLE y PADDING_ANGLE
//   const { startAngles, endAngles } = useMemo(() => {
//     const count = displayedSeries?.length;
//     if (count === 0) return { startAngles: [], endAngles: [] };

//     // Definir pesos segÃºn count
//     let weights = [];
//     if (count === 1) {
//       weights = [1];
//     } else if (count === 2) {
//       weights = [1, 1];
//     } else if (count === 3) {
//       weights = [2, 1, 1];
//     } else {
//       weights = [1, 1, 1, 1];
//     }

//     const totalWeight = weights.reduce((sum, w) => sum + w, 0);
//     const totalGapSum = count * GAP_ANGLE;
//     const totalPaddingSum = count * 2 * PADDING_ANGLE;
//     const availableArc = (END_ANGLE - START_ANGLE) - totalGapSum - totalPaddingSum;
//     const unitArc = availableArc / totalWeight;
//     const segmentFullArc = weights.map((w) => 2 * PADDING_ANGLE + w * unitArc);

//     const startAngles = [];
//     const endAngles = [];
//     let cursor = GAP_ANGLE / 2;

//     for (let i = 0; i < count; i++) {
//       const sA = cursor;
//       const eA = sA + segmentFullArc[i];
//       startAngles.push(sA);
//       endAngles.push(eA);
//       cursor = eA + GAP_ANGLE;
//     }

//     return { startAngles, endAngles };
//   }, [displayedSeries]);

//   const renderDayBarsProps = {displayedSeries,colors, startAngles,endAngles,PADDING_ANGLE,R_INNER,R_MID_INNER,visibleMetrics,setHoverDayInfo,setHoverInfo,setSelectedInfo,polarToCartesian}
//   const renderBubbleSeriesProps = {displayedSeries, startAngles,endAngles,PADDING_ANGLE,visibleMetrics,setHoverDayInfo,setHoverInfo,setSelectedInfo,metricMaxima,computeMetricRadius,hoverInfo,selectedInfo, mapValueToBubbleRadius,polarToCartesian}
//   const renderDashedArcsProps = { displayedSeries, startAngles,endAngles,PADDING_ANGLE,visibleMetrics,computeMetricRadius,polarToCartesian}
//   const renderOuterArcsAndLabelsProps = {displayedSeries, startAngles,endAngles,colors,R_OUTER,describeOuterArc,polarToCartesian}
//   const renderSelectedTableProps = {displayedSeries,selectedInfo,colors,CY,CX }
//   const renderAggregatesCentralProps = {colors,aggregatedBySeries}

//   return (
//     <div
//       style={{
//         height: "auto",
//         display: "flex",
//         flexDirection: "column",
//         alignItems: "center",
//         borderRadius: "1vw",
//         paddingBottom: "2vh",
//         marginTop: "-1vh",
//       }}
//     >
//       <div
//         style={{
//           width: '33vw',
//           display: 'flex',
//           justifyContent: 'center',
//           alignContent: 'center',
//           alignItems: 'center',
//           flexDirection: 'row',
//           marginBottom: "-1.5vh",
//         }}
//       >
//         {renderAggregatesCentral(renderAggregatesCentralProps)}
//       </div>

//       <svg
//         style={{ marginLeft: '-1vw' }}
//         width="32vw"
//         height="25vw"
//         viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
//       >
//         <defs>
//           <radialGradient id="grad-inner" cx="50%" cy="50%" r="50%">
//             <stop offset="0%" stopColor="rgba(30, 23, 53, 0.3)" />
//             <stop offset="100%" stopColor="rgba(69, 68, 93, 0.3)" />
//           </radialGradient>
//           <radialGradient id="grad-mid" cx="50%" cy="50%" r="50%">
//             <stop offset="0%" stopColor="rgba(69, 68, 93, 0.3)" />
//             <stop offset="100%" stopColor="rgba(31, 24, 54, 0.3)" />
//           </radialGradient>
//         </defs>

//         {/* Pane exterior â€“ anillo oscuro */}
//         <circle
//           cx={CX}
//           cy={CY}
//           r={R_OUTER}
//           fill="rgba(70, 70, 92, 0.05)"
//           stroke="rgba(46, 44, 58, 0.05)"
//           strokeWidth="2"
//         />
//         <circle
//           cx={CX}
//           cy={CY}
//           r={R_OUTER_INNER}
//           fill="rgba(31, 24, 54, 0.05)"
//           stroke="none"
//         />

//         {/* Arcos exteriores y etiquetas de jaula */}
//         {renderOuterArcsAndLabels(renderOuterArcsAndLabelsProps)}

//         {/* Pane medio â€“ fondo de mÃ©tricas */}
//         <path
//           d={(() => {
//             const angleStart = START_ANGLE;
//             const angleEnd = END_ANGLE;
//             const p1 = polarToCartesian(R_MID, angleStart, );
//             const p2 = polarToCartesian(R_MID, angleEnd, );
//             const p3 = polarToCartesian(R_MID_INNER, angleEnd, );
//             const p4 = polarToCartesian(R_MID_INNER, angleStart, );
//             const largeArcFlag = angleEnd - angleStart <= 180 ? "0" : "1";
//             return [
//               "M", p1.x.toFixed(3), p1.y.toFixed(3),
//               "A", R_MID.toFixed(3), R_MID.toFixed(3), 0, largeArcFlag, 1, p2.x.toFixed(3), p2.y.toFixed(3),
//               "L", p3.x.toFixed(3), p3.y.toFixed(3),
//               "A", R_MID_INNER.toFixed(3), R_MID_INNER.toFixed(3), 0, largeArcFlag, 0, p4.x.toFixed(3), p4.y.toFixed(3),
//               "Z"
//             ].join(" ");
//           })()}
//           fill="url(#grad-mid)"
//           stroke="none"
//         />

//         {/* Pane interior â€“ cÃ­rculo central */}
//         <circle
//           cx={CX}
//           cy={CY}
//           r={R_INNER}
//           fill="url(#grad-inner)"
//           stroke="#2E2C3A"
//           strokeWidth="1"
//         />

//         {/* Arcos punteados por mÃ©trica */}
//         {renderDashedArcs(renderDashedArcsProps)}

//         {/* Barras radiales de dÃ­a */}
//         {renderDayBars(renderDayBarsProps)}

//         {/* Burbujas de mÃ©tricas */}
//         {renderBubbleSeries(renderBubbleSeriesProps)}

//         {/* Tooltip central en hover de una burbuja */}
//         {hoverInfo && !selectedInfo && (
//           <g>
//             <text
//               x={CX}
//               y={CY - 35}
//               textAnchor="middle"
//               fontSize="1vw"
//               fill="#e84e0e"
//               fontWeight="700"
//             >
//               {t('day', 'Dia')}: {hoverInfo.day}
//             </text>
//             <text
//               x={CX}
//               y={CY - 5}
//               textAnchor="middle"
//               fontSize="0.9vw"
//               fill="#c3c3c3"
//               fontWeight="600"
//             >
//               {hoverInfo.seriesName}
//             </text>
//             <text
//               x={CX}
//               y={CY + 25}
//               textAnchor="middle"
//               fontSize="0.63vw"
//               fill={ALL_METRICS[hoverInfo.metricKey]}
//               fontWeight="600"
//             >
//               {`${hoverInfo.metricLabel}:`}
//             </text>
//             <text
//               x={CX}
//               y={CY + 50}
//               textAnchor="middle"
//               fontSize="0.63vw"
//               fill={ALL_METRICS[hoverInfo.metricKey]}
//               fontWeight="600"
//             >
//               {`${hoverInfo.metricValue}`}
//             </text>
//           </g>
//         )}

//         {/* Tooltip central en hover de â€œbarraâ€ (todos los valores) */}
//         {hoverDayInfo && selectedInfo === null && (
//           <g>
//             <text
//               x={CX}
//               y={CY - 65}
//               textAnchor="middle"
//               fontSize="1vw"
//               fill="#e84e0e"
//               fontWeight="700"
//             >
//               {t('day', 'Dia')}: {hoverDayInfo.day}
//             </text>
//             <text
//               x={CX}
//               y={CY - 35}
//               textAnchor="middle"
//               fontSize="0.9vw"
//               fill="#c3c3c3"
//               fontWeight="600"
//             >
//               {hoverDayInfo.seriesName}
//             </text>
//             {hoverDayInfo.allValues.map((mObj, i) => (
//               <text
//                 key={`all-${i}`}
//                 x={CX}
//                 y={CY + (i * 25) - 2}
//                 textAnchor="middle"
//                 fontSize="0.63vw"
//                 fill={mObj.color}
//                 fontWeight="500"
//               >
//                 {`${mObj.label}: ${mObj.value}`}
//               </text>
//             ))}
//           </g>
//         )}
//         {/* CUANDO selectedInfo existe, mostramos tabla de valores */}
//         {renderSelectedTable(renderSelectedTableProps)}
//       </svg>
//     </div>
//   );
// };

// export default AdvancedPolarChart;



